import {
    type ComboboxItem,
    type ComboboxParsedItem,
    type ComboboxParsedItemGroup,
    MultiSelect,
    type MultiSelectProps,
} from '@mantine/core';
import type { Except, Merge, SetRequired } from 'type-fest';
import { useCommonInputProps } from '../../../hooks/useCommonInputProps';
import { useField } from '../../../hooks/useField';
import { filterArrayByString, sortBy } from '../../../utils/utilFunctions';
import type { Controlled_ValidatedInputProps } from '../../inputs/controlled/types';

const isArrayOfComboboxItems = (
    options: Array<ComboboxParsedItem>
): options is Array<ComboboxItem> => options.every((it) => 'label' in it);

const isArrayOfComboboxParsedItemGroups = (
    options: Array<ComboboxParsedItem>
): options is Array<ComboboxParsedItemGroup> =>
    options.every((it) => 'items' in it);

type MultiSelectInputProps = Merge<
    Except<
        SetRequired<MultiSelectProps, 'data' | 'readOnly'>,
        'styles' | 'rightSectionWidth' | 'rightSection'
    >,
    Except<Controlled_ValidatedInputProps<false, Array<string>>, 'nullable'> & {
        placeholderOnlyWithoutSelection?: boolean;
    }
>;

const MultiSelectInput = ({
    onChange,
    label: _label,
    labelInfo,
    severity,
    helperText,
    endingIcon,
    filter,
    placeholder: _placeholder,
    placeholderOnlyWithoutSelection,
    readOnly,
    searchable,
    value,
    ...props
}: MultiSelectInputProps) => {
    const { rightSectionWidth, rightSection, inputStyles, label, placeholder } =
        useCommonInputProps({
            endingIcon,
            severity,
            helperText,
            nullable: false,
            onChange,
            value,
            label: _label,
            labelInfo,
            placeholder: _placeholder,
            readOnly,
            clearable: !!value.length,
            defaultValue: [],
        });

    return (
        <MultiSelect
            {...props}
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            searchable={searchable ?? false}
            filter={
                filter ??
                (({ options, search }) => {
                    if (!options.length) {
                        return options;
                    }

                    if (isArrayOfComboboxItems(options)) {
                        return filterArrayByString(
                            search,
                            options,
                            ({ label }) => label
                        ).toSorted(sortBy((i) => i.label));
                    }

                    if (isArrayOfComboboxParsedItemGroups(options)) {
                        return options.mapNotNull(({ items, group }) => {
                            const filteredItems = filterArrayByString(
                                search,
                                items,
                                ({ label }) => label
                            ).toSorted(sortBy((i) => i.label));

                            return filteredItems.length ?
                                    { group, items: filteredItems }
                                :   null;
                        });
                    }

                    return options;
                })
            }
            label={label}
            placeholder={
                placeholderOnlyWithoutSelection && value.length ?
                    undefined
                :   placeholder
            }
            rightSectionWidth={rightSectionWidth}
            rightSection={rightSection}
            styles={{ input: inputStyles }}
            // w={'100%'}
            // comboboxProps={{ zIndex: 9000 }}
            nothingFoundMessage={'Nothing found...'}
        />
    );
};

export type FormMultiSelectInputProps<T extends object> = Merge<
    Omit<MultiSelectInputProps, keyof useField.Result<Array<string>>>,
    useField.Props<Array<string>, T>
>;

const FormMultiSelectInput = <T extends object>({
    name,
    control,
    onChange,
    ...props
}: FormMultiSelectInputProps<T>) => {
    const fieldProps = useField({
        name,
        control,
        onChange,
    });

    return (
        <MultiSelectInput
            {...props}
            {...fieldProps}
        />
    );
};

MultiSelectInput.Form = FormMultiSelectInput;

export { MultiSelectInput };
